import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const empty = { code: "", name: "", address: "", status: "kosong", rent_amount: 0, rent_period: "monthly", property_id: "", notes: "" };

const STATUS_TONE = {
  kosong: "bg-secondary text-secondary-foreground",
  terisi: "bg-success/15 text-success",
  maintenance: "bg-warning/15 text-warning",
};

export default function Units() {
  const { data: units = [], isLoading } = useSWR("/units", fetcher);
  const { data: properties = [] } = useSWR("/properties", fetcher);
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const propertyMap = Object.fromEntries(properties.map((p) => [p.id, p]));
  const filtered = propertyFilter === "all"
    ? units
    : propertyFilter === "none"
    ? units.filter((u) => !u.property_id)
    : units.filter((u) => u.property_id === propertyFilter);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...u, property_id: u.property_id || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, rent_amount: Number(form.rent_amount) || 0, property_id: form.property_id || null };
      if (editing) {
        await api.put(`/units/${editing.id}`, body);
        toast.success("Unit diperbarui");
      } else {
        await api.post("/units", body);
        toast.success("Unit ditambahkan");
      }
      setOpen(false);
      mutate("/units");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    if (!confirm(`Hapus unit ${u.code}?`)) return;
    try {
      await api.delete(`/units/${u.id}`);
      toast.success("Unit dihapus");
      mutate("/units");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="units-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Data Master
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Unit Kontrakan</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola daftar unit, status, dan tarif sewa.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-unit-button">
          <Plus className="h-4 w-4" /> Tambah Unit
        </Button>
      </header>

      {properties.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="property-filter-bar">
          <button
            onClick={() => setPropertyFilter("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${propertyFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-secondary"}`}
            data-testid="property-filter-all"
          >
            Semua ({units.length})
          </button>
          {properties.map((p) => (
            <button
              key={p.id}
              onClick={() => setPropertyFilter(p.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${propertyFilter === p.id ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-secondary"}`}
              data-testid={`property-filter-${p.id}`}
            >
              {p.name} ({units.filter((u) => u.property_id === p.id).length})
            </button>
          ))}
          <button
            onClick={() => setPropertyFilter("none")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${propertyFilter === "none" ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-secondary"}`}
          >
            Tanpa properti ({units.filter((u) => !u.property_id).length})
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : filtered.length === 0 ? (
        <EmptyState onAdd={openNew} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((u) => (
            <Card key={u.id} className="hover:shadow-sm transition-shadow" data-testid={`unit-card-${u.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-md font-mono ${STATUS_TONE[u.status] || ""}`}>{u.code}</Badge>
                      <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider">
                        {u.status}
                      </Badge>
                    </div>
                    <div className="font-display text-lg font-medium mt-2 truncate">{u.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{u.address || "—"}</div>
                    {u.property_id && propertyMap[u.property_id] && (
                      <div className="text-[10px] uppercase tracking-[0.14em] text-primary mt-1 truncate">
                        {propertyMap[u.property_id].name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sewa</div>
                    <div className="font-display text-xl font-medium tracking-tight">{formatIDR(u.rent_amount)}</div>
                    <div className="text-xs text-muted-foreground">per {u.rent_period === "monthly" ? "bulan" : "tahun"}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-unit-${u.code}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(u)} data-testid={`delete-unit-${u.code}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Unit" : "Tambah Unit"}</DialogTitle>
            <DialogDescription>Kode unit harus unik. Nilai sewa dipakai sebagai default saat membuat kontrak.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Kode Unit</Label>
                <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="unit-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="unit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kosong">Kosong</SelectItem>
                    <SelectItem value="terisi">Terisi</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nama Unit</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="unit-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Alamat</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            {properties.length > 0 && (
              <div className="space-y-1.5">
                <Label>Properti (opsional)</Label>
                <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="unit-property-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tanpa properti —</SelectItem>
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nilai Sewa (Rp)</Label>
                <Input type="number" min="0" required value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} data-testid="unit-rent-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Periode</Label>
                <Select value={form.rent_period} onValueChange={(v) => setForm({ ...form, rent_period: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Bulanan</SelectItem>
                    <SelectItem value="yearly">Tahunan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="unit-save-button">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <Card className="border-dashed">
      <CardContent className="p-12 flex flex-col items-center text-center gap-4">
        <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
          <Building2 className="h-7 w-7" />
        </div>
        <div>
          <div className="font-display text-lg font-medium">Belum ada unit</div>
          <p className="text-sm text-muted-foreground mt-1">Mulai dengan menambahkan unit kontrakan pertama Anda.</p>
        </div>
        <Button onClick={onAdd} className="gap-2"><Plus className="h-4 w-4" /> Tambah Unit</Button>
      </CardContent>
    </Card>
  );
}
